import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PasswordStrength } from "@/components/PasswordStrength";
import { takeSource, fireSignupConversion } from "@/lib/marketing";

const HERO = "https://images.unsplash.com/photo-1553010254-267b14135d9b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(["register", "forgot"].includes(params.get("mode")) ? params.get("mode") : "login");
  const creatorJoin = params.get("creator") === "1";
  const [inviter, setInviter] = useState("");
  useEffect(() => {
    const code = localStorage.getItem("velluto_invite");
    if (code) api.get(`/society/invite/${encodeURIComponent(code)}`).then(({ data }) => data.valid && setInviter(data.inviter)).catch(() => {});
  }, []);
  const [remember, setRemember] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [form, setForm] = useState({ email: "", password: "", display_name: "", age: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const nav = useNavigate();
  const takeNext = () => { const n = sessionStorage.getItem("velluto_next"); sessionStorage.removeItem("velluto_next"); return n; };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge_id: challenge.challenge_id, code: code.trim() });
      setUser(data); toast.success("Welcome back"); nav(takeNext() || "/hub");
    } catch (e2) {
      const msg = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      setError(msg);
      if (/expired|Too many/i.test(msg)) { setChallenge(null); setCode(""); }
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link is on its way");
        setMode("login");
      } else if (mode === "register") {
        const { data } = await api.post("/auth/register", {
          email: form.email,
          password: form.password,
          display_name: form.display_name,
          age: parseInt(form.age, 10),
          ref: localStorage.getItem("velluto_ref") || undefined,
          invite: localStorage.getItem("velluto_invite") || undefined,
          source: takeSource(),
        });
        localStorage.removeItem("velluto_ref");
        localStorage.removeItem("velluto_invite");
        fireSignupConversion();
        setUser(data);
        toast.success("Welcome to Velluto — check your inbox to confirm your email");
        nav(data.email_verified === false ? "/confirm-email" : (takeNext() || "/me"));
      } else {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password, remember });
        if (data.two_factor_required) { setChallenge(data); setLoading(false); return; }
        setUser(data);
        toast.success("Welcome back");
        nav(takeNext() || "/hub");
      }
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/15 py-3 text-lg focus:border-wine outline-none transition-colors placeholder:text-text-muted/50";

  return (
    <div className="min-h-screen grid md:grid-cols-2 pt-16">
      <div className="relative hidden md:block">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/40 to-ink" />
        <div className="absolute bottom-16 left-12 z-10">
          <p className="font-display text-5xl font-light leading-none max-w-sm">
            "The best secrets<br /><span className="italic text-gold">are shared."</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-sm">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">
            {creatorJoin && mode === "register" ? "Creator sign-up" : mode === "register" ? "Request access" : mode === "forgot" ? "Recover access" : "Members entrance"}
          </p>
          <h1 className="font-display text-5xl font-light mb-10">
            {mode === "register" ? "Create your profile" : mode === "forgot" ? "Reset your password" : "Welcome back"}
          </h1>
          {creatorJoin && mode === "register" && (
            <p data-testid="creator-join-note" className="-mt-6 mb-8 text-sm text-text-muted">Step 1 of 2. Create your account, then finish your creator application. The first 300 approved creators keep 100% of their earnings for 3 months.</p>
          )}
          {params.get("vip") === "1" && mode === "register" && (
            <p data-testid="vip-join-note" className="-mt-6 mb-8 text-sm text-text-muted">
              {inviter ? <><span className="text-gold">{inviter}</span> invited you to the society. Activate Basic and you get a week of Premium free. </> : null}
              Create your account, then activate Basic. The first 300 Basic members get <span className="text-gold">Premium free for 1 month</span>.
            </p>
          )}

          {challenge ? (
          <form data-testid="two-factor-step" onSubmit={submitCode} className="space-y-6">
            <p className="text-sm text-text-muted">We emailed a 6-digit code to <span className="text-text-main">{challenge.email_hint}</span>. Enter it to finish signing in.</p>
            <input data-testid="two-factor-code" className={`${field} tracking-[0.5em] text-2xl font-mono`} inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus required />
            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="two-factor-submit" disabled={loading || code.length !== 6} className="w-full py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em] disabled:opacity-50">{loading ? "..." : "Verify code"}</button>
            <button type="button" data-testid="two-factor-back" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="w-full text-xs text-text-muted hover:text-text-main">Back to sign in</button>
          </form>
          ) : (
          <form onSubmit={submit} className="space-y-6">
            {mode === "register" && (
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="input-name" className={field} placeholder="Display name" value={form.display_name} onChange={set("display_name")} required />
                <input data-testid="input-age" className={field} type="number" min="18" placeholder="Age" value={form.age} onChange={set("age")} required />
              </div>
            )}
            <input data-testid="input-email" className={field} type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
            {mode !== "forgot" && (
              <div>
                <input data-testid="input-password" className={field} type="password" placeholder="Password" value={form.password} onChange={set("password")} required minLength={mode === "register" ? 8 : undefined} autoComplete={mode === "register" ? "new-password" : "current-password"} />
                {mode === "register" && <PasswordStrength value={form.password} />}
              </div>
            )}
            {mode === "login" && (
              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-xs text-text-muted cursor-pointer select-none">
                  <input data-testid="remember-me" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#D4AF37] w-3.5 h-3.5" />
                  Keep me signed in for 30 days
                </label>
                <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); }}
                  className="text-xs text-text-muted hover:text-text-main transition-colors">
                  Forgot your password?
                </button>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

            <button
              data-testid="auth-submit"
              disabled={loading}
              className="w-full py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {loading ? "..." : mode === "register" ? "Enter Velluto" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </button>
          </form>
          )}

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-4 my-7">
                <span className="h-px flex-1 bg-white/10" />
                <span className="text-xs uppercase tracking-[0.2em] text-text-muted">or</span>
                <span className="h-px flex-1 bg-white/10" />
              </div>
              <button
                type="button"
                data-testid="google-signin"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin;
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="w-full py-4 rounded-full border border-white/15 hover:border-white/30 hover:bg-white/5 transition-colors flex items-center justify-center gap-3 text-sm"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
                Continue with Google
              </button>
            </>
          )}

          <p className="mt-8 text-sm text-text-muted">
            {mode === "register" ? "Already a member? " : mode === "forgot" ? "Remembered it? " : "New here? "}
            <button
              data-testid="auth-toggle"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-text-main underline underline-offset-4 hover:text-gold transition-colors"
            >
              {mode === "register" ? "Sign in" : mode === "forgot" ? "Back to sign in" : "Request access"}
            </button>
          </p>
          <Link to="/" className="mt-4 inline-block text-xs text-text-muted hover:text-text-main transition-colors">← Back home</Link>
        </motion.div>
      </div>
    </div>
  );
}
